import React, { createContext, useState, useContext, useEffect, useMemo, useRef, useCallback } from 'react';
import DBTable from '../handlers/DatabaseHandler';
import { useTranslation } from 'react-i18next';
import apiClient from '../utils/apiClient.js';
import { COLLECTION } from '../config/mongoCollections.js';
import { isFounder, isInternalTeam, isMember } from '../utils/internalAccounts.js';
import {
  getActiveFeatures,
  hasFeature as userHasFeature,
  isFeatureTrialUser
} from '../utils/userFeatureAccess.js';
import { resolveLanguagePreference, normalizeLanguage } from '../utils/languageUtils';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export { isFounder, isMember, isInternalTeam };
/** @deprecated Use isInternalTeam — founders and members, not school 主任. */
export const isPresenter = isInternalTeam;

export function isNotPresenter(user) {
  return !isInternalTeam(user);
}

export function isStaff(user) {
  return Boolean(user?.User_Role === 'Sta');
}

export function isStaffOrPresenter(user) {
  return isStaff(user) || isInternalTeam(user);
}

export function isVerifiedSchoolStaff(user) {
  return Boolean(
    user?.affiliation_status === 'verified' &&
    user?.school_id &&
    (user?.User_Role === 'Sta' || user?.User_Role === 'Tea')
  );
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const previousUserRef = useRef(null);
  const hasSyncedLanguageRef = useRef(false);
  const { t } = useTranslation();

  // Memoize the currentUser to prevent unnecessary re-renders
  const memoizedCurrentUser = useMemo(() => {
    // console.log('AuthContext: Memoizing currentUser:', currentUser?.User_ID);
    return currentUser;
  }, [
    currentUser?.User_ID,
    currentUser?.ability?.join(','),
    currentUser?.ability_6d?.join(','),
    currentUser?.eddy_recommendation_title,
    currentUser?.eddy_recommendation_text,
    currentUser?.needs_recommendation_regeneration,
    currentUser?.jupas_programme_order ? JSON.stringify(currentUser.jupas_programme_order) : '',
    currentUser?.access_mode,
    currentUser?.feature_access ? JSON.stringify(currentUser.feature_access) : '',
    currentUser?.preferred_language
  ]);

  const userTable = new DBTable(
    COLLECTION.users,
    "User_ID",
    {
      User_ID: "",
      First_Name: "",
      Last_Name: "",
      Nickname: "",
      Email_Address: "",
      Tel: "",
      User_Role: "",
      School_Name: "",
      School_District: "",
      Form: "",
      school_id: "",
      affiliation_status: "none",
      affiliation_method: null,
      direct_marketing: false,
      email_list: false,
      card_id: "",
      ability_6d: [1.5, 1.5, 1.5, 1.5, 1.5, 1.5],
      eddy_recommendation_title: "",
      eddy_recommendation_text: "",
      needs_recommendation_regeneration: true,
      trajectory_events: [],
      trajectory_connections: [],
      trajectory_analyses: [],
      event_types: ['academic', 'extracurricular', 'personal'],
      preferred_language: "en",
      jupas_programme_order: {}
    }
  );

  const fetchCompleteUserProfile = async (userId) => {
    try {
      // console.log('AuthContext: Fetching complete user profile for:', userId);
      const userData = await userTable.handleRead({ User_ID: userId }, false);
      // console.log('AuthContext: Complete user profile:', userData);
      if (userData) {
        // console.log('AuthContext: Trajectory data - events:', userData.trajectory_events?.length || 0);
        // console.log('AuthContext: Trajectory data - connections:', userData.trajectory_connections?.length || 0);
        // console.log('AuthContext: Trajectory data - analyses:', userData.trajectory_analyses?.length || 0);
        // console.log('AuthContext: Trajectory data - event_types:', userData.event_types);
      }
      return userData;
    } catch (error) {
      console.error('AuthContext: Error fetching complete user profile:', error);
      return null;
    }
  };

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    // console.log('AuthContext: Checking token:', token);
    if (token) {
      // First fetch basic user data using the token.
      // Avoid aggressive 429 retries — they burn the remaining auth budget.
      apiClient.get('/api/auth/me', {}, { maxRetries: 1, baseDelay: 2000 })
        .then(response => {
          if (response.status === 429) {
            const error = new Error('Too many requests while loading profile');
            error.status = 429;
            throw error;
          }
          if (!response.ok) {
            const error = new Error('Failed to fetch user data');
            error.status = response.status;
            throw error;
          }
          return response.json();
        })
        .then(basicUserData => {
          // console.log('AuthContext: Fetched basic user data:', basicUserData);
          // Check if user data is empty but we have a token
          if (!basicUserData || Object.keys(basicUserData).length === 0) {
            // console.log('AuthContext: Empty profile detected, reloading...');
            // Force reload the page to trigger a fresh auth check
            window.location.reload();
            return;
          }

          // Now fetch the complete user profile from database
          return fetchCompleteUserProfile(basicUserData.User_ID);
        })
        .then(completeUserData => {
          if (completeUserData) {
            const targetLanguage = resolveLanguagePreference({
              dbLanguage: completeUserData.preferred_language
            });
            // Keep in-memory/cache aligned with resolved intent for this device,
            // but do not overwrite the DB — a stale local leftover must not
            // clobber the account preference on a shared/new browser.
            const alignedUser =
              normalizeLanguage(completeUserData.preferred_language) === targetLanguage
                ? completeUserData
                : { ...completeUserData, preferred_language: targetLanguage };

            setCurrentUserStable(alignedUser);
            setIsAuthenticated(true);
            localStorage.setItem('user', JSON.stringify(alignedUser));

            if (!hasSyncedLanguageRef.current) {
              hasSyncedLanguageRef.current = true;
              import('../i18n').then(({ default: i18n }) => {
                if (normalizeLanguage(i18n.language) !== targetLanguage) {
                  i18n.changeLanguage(targetLanguage);
                  setTimeout(() => {
                    if (normalizeLanguage(i18n.language) !== targetLanguage) {
                      window.location.reload();
                    }
                  }, 500);
                }
              });
            }
          } else {
            console.error('AuthContext: Failed to fetch complete user profile');
            localStorage.removeItem('token');
            setIsAuthenticated(false);
          }
        })
        .catch(error => {
          console.error('Error fetching user data:', error);
          // Rate limits are temporary — keep the session instead of logging the user out.
          if (error?.status === 429) {
            try {
              const cached = JSON.parse(localStorage.getItem('user') || 'null');
              if (cached?.User_ID) {
                setCurrentUserStable(cached);
                setIsAuthenticated(true);
                return;
              }
            } catch (_) {
              // ignore corrupt cache
            }
            setIsAuthenticated(Boolean(localStorage.getItem('token')));
            return;
          }
          localStorage.removeItem('token');
          setIsAuthenticated(false);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // console.log('AuthContext: No token found');
      setIsAuthenticated(false);
      setLoading(false);
    }
  }, []);

  const login = async (credentials, tOverride) => {
    const tFunc = tOverride || t;
    try {
      const identifier = credentials.identifier.trim();
      // Map identifier to either email or username
      const loginData = {
        password: credentials.password
      };

      // Check if identifier is an email (contains @) or username
      if (identifier.includes('@')) {
        loginData.email = identifier;
      } else {
        loginData.username = identifier;
      }

      const response = await apiClient.post('/api/auth/login', loginData);

      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        let message = '';

        if (contentType.includes('application/json')) {
          const errorData = await response.json();
          if (Array.isArray(errorData.errors) && errorData.errors.length > 0) {
            message = errorData.errors.map(error => error.msg).filter(Boolean).join('. ');
          }
          message = message || errorData.message || '';
        } else {
          message = await response.text();
        }

        if (response.status === 429 || message.toLowerCase().includes('too many')) {
          throw new Error(tFunc('auth.login.tooManyAttempts'));
        }

        throw new Error(message || tFunc('auth.login.failed'));
      }

      const data = await response.json();
      // console.log('AuthContext: Login response:', data);
      localStorage.setItem('token', data.token);

      // Fetch complete user profile after login
      const completeUserData = await fetchCompleteUserProfile(data.user.User_ID);
      if (completeUserData) {
        // On login, account preference wins when local has no explicit choice;
        // resolveLanguagePreference still prefers a present i18nextLng.
        const targetLanguage = resolveLanguagePreference({
          dbLanguage: completeUserData.preferred_language
        });
        const alignedUser =
          normalizeLanguage(completeUserData.preferred_language) === targetLanguage
            ? completeUserData
            : { ...completeUserData, preferred_language: targetLanguage };

        setCurrentUserStable(alignedUser);
        setIsAuthenticated(true);
        localStorage.setItem('user', JSON.stringify(alignedUser));
        localStorage.removeItem('languageInitialized');

        if (!hasSyncedLanguageRef.current) {
          hasSyncedLanguageRef.current = true;
          import('../i18n').then(({ default: i18n }) => {
            if (normalizeLanguage(i18n.language) !== targetLanguage) {
              i18n.changeLanguage(targetLanguage);
              setTimeout(() => {
                if (normalizeLanguage(i18n.language) !== targetLanguage) {
                  window.location.reload();
                }
              }, 500);
            }
          });
        }
      } else {
        // console.log('AuthContext: Using basic user data after login:', data.user);
        setCurrentUserStable(data.user);
        setIsAuthenticated(true);

        // Store basic user data in localStorage
        localStorage.setItem('user', JSON.stringify(data.user));

        // Clear language initialization flag to ensure proper language sync after login
        localStorage.removeItem('languageInitialized');
      }

      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('languageInitialized');
    setCurrentUserStable(null);
    setIsAuthenticated(false);
    hasSyncedLanguageRef.current = false;
  };

  const register = async (userData) => {
    try {
      const response = await apiClient.post('/api/register', userData);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || t('auth.register.failed'));
      }

      // Registration successful - automatically log in the user
      // console.log('AuthContext: Registration successful, logging in user:', data);

      // Store the token
      localStorage.setItem('token', data.token);

      // Fetch complete user profile after registration
      const completeUserData = await fetchCompleteUserProfile(data.user.User_ID);
      if (completeUserData) {
        // console.log('AuthContext: Setting complete user data after registration:', completeUserData);
        setCurrentUserStable(completeUserData);
        setIsAuthenticated(true);

        // Clear language initialization flag to ensure proper language sync after registration
        localStorage.removeItem('languageInitialized');
      } else {
        // console.log('AuthContext: Using basic user data after registration:', data.user);
        setCurrentUserStable(data.user);
        setIsAuthenticated(true);

        // Clear language initialization flag to ensure proper language sync after registration
        localStorage.removeItem('languageInitialized');
      }

      return true;
    } catch (error) {
      console.error('Registration error:', error);
      // If the error is from the server response, use that message
      if (error.message) {
        throw new Error(error.message);
      }
      // Otherwise, throw a generic error
      throw new Error(t('auth.register.failed'));
    }
  };

  // Stable setCurrentUser function that only updates when there are actual changes
  const setCurrentUserStable = (newUser) => {
    const prevUser = previousUserRef.current;

    const hasChanged = !prevUser ||
      prevUser.User_ID !== newUser?.User_ID ||
      JSON.stringify(prevUser.ability) !== JSON.stringify(newUser?.ability) ||
      JSON.stringify(prevUser.ability_6d) !== JSON.stringify(newUser?.ability_6d) ||
      prevUser.eddy_recommendation_title !== newUser?.eddy_recommendation_title ||
      prevUser.eddy_recommendation_text !== newUser?.eddy_recommendation_text ||
      prevUser.needs_recommendation_regeneration !== newUser?.needs_recommendation_regeneration ||
      JSON.stringify(prevUser.jupas_programme_order || {}) !== JSON.stringify(newUser?.jupas_programme_order || {}) ||
      prevUser.access_mode !== newUser?.access_mode ||
      JSON.stringify(prevUser.feature_access || []) !== JSON.stringify(newUser?.feature_access || []) ||
      prevUser.preferred_language !== newUser?.preferred_language;

    if (hasChanged) {
      previousUserRef.current = newUser;
      setCurrentUser(newUser);
    }
  };

  // Function to update language preference locally without triggering full re-fetch
  const updateLanguagePreference = (newLanguage) => {
    const normalized = normalizeLanguage(newLanguage) || newLanguage;
    if (currentUser) {
      const updatedUser = { ...currentUser, preferred_language: normalized };
      setCurrentUser(updatedUser);
      previousUserRef.current = updatedUser;
      try {
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } catch {
        // Ignore storage failures
      }
    }
  };

  /** Re-fetch full profile from DB (e.g. after JUPAS order save). Keeps nav / profile UI in sync. */
  const refreshUserProfile = useCallback(async () => {
    const userId = currentUser?.User_ID;
    if (!userId) return;
    const complete = await fetchCompleteUserProfile(userId);
    if (complete) {
      setCurrentUserStable(complete);
      localStorage.setItem('user', JSON.stringify(complete));
    }
  }, [currentUser?.User_ID]);

  const value = useMemo(() => {
    // console.log('AuthContext: Creating new context value for user:', memoizedCurrentUser?.User_ID);
    return {
      currentUser: memoizedCurrentUser,
      isAuthenticated,
      login,
      logout,
      register,
      loading,
      updateLanguagePreference,
      refreshUserProfile,
      hasFeature: (feature) => userHasFeature(memoizedCurrentUser, feature),
      getActiveFeatures: () => getActiveFeatures(memoizedCurrentUser),
      isFeatureTrialUser: () => isFeatureTrialUser(memoizedCurrentUser)
    };
  }, [memoizedCurrentUser, isAuthenticated, loading, refreshUserProfile]);

  // console.log('AuthContext: Providing value:', value);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
} 
